import * as React from "react";
import { useMutation } from "convex/react";
import { toast } from "sonner";
import { Edit } from "lucide-react";
import { api } from "$convex/convexApi";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type RenameFolderDialogProps = {
  authUserId: string;
  folderPath: string;
  currentName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (newPath: string) => void;
};

export function RenameFolderDialog({
  authUserId,
  folderPath,
  currentName,
  open,
  onOpenChange,
  onSuccess,
}: RenameFolderDialogProps) {
  const [newName, setNewName] = React.useState(currentName);
  const [isRenaming, setIsRenaming] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const renameFolder = useMutation(api.knowledgeBase.renameFolder);

  const handleRename = React.useCallback(async () => {
    if (!newName.trim()) {
      setError("Folder name is required");
      return;
    }

    if (newName.trim() === currentName) {
      onOpenChange(false);
      return;
    }

    setIsRenaming(true);
    setError(null);

    try {
      const result = await renameFolder({
        authUserId,
        oldPath: folderPath,
        newName: newName.trim(),
      });

      toast.success(result.message);
      onOpenChange(false);
      onSuccess?.(result.newPath);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to rename folder";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsRenaming(false);
    }
  }, [authUserId, currentName, folderPath, newName, onOpenChange, onSuccess, renameFolder]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter" && !isRenaming) {
        event.preventDefault();
        handleRename();
      }
    },
    [handleRename, isRenaming],
  );

  React.useEffect(() => {
    if (open) {
      setNewName(currentName);
      setError(null);
    }
  }, [open, currentName]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="size-5" />
            Rename Folder
          </DialogTitle>
          <DialogDescription>
            Rename "{currentName}" and update all nested documents
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="new-folder-name">New Folder Name</Label>
            <Input
              id="new-folder-name"
              placeholder="Enter new folder name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isRenaming}
              autoFocus
            />
            {error ? (
              <p className="text-sm text-destructive">{error}</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Use letters, numbers, spaces, hyphens, and underscores only. No slashes allowed.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isRenaming}
          >
            Cancel
          </Button>
          <Button onClick={handleRename} disabled={isRenaming || !newName.trim()}>
            {isRenaming ? "Renaming..." : "Rename Folder"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
